import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { storage } from '@/lib/storage';
import { useAuth } from '@/contexts/AuthContext';
import { websocketClient } from '@/lib/websocket-client';

export type JackpotTier = 'grand' | 'major' | 'minor' | 'mini';
export type CurrencyType = 'GC' | 'SC';

interface JackpotData {
  amount: number;
  optInCost: number;
  isOptedIn: boolean;
  color: string;
  participants: number;
}

interface JackpotWin {
  id: string;
  jackpot_tier: string;
  currency_type: string;
  amount: number;
  created_at: string;
}

interface JackpotContextType {
  gcJackpots: Record<JackpotTier, JackpotData>;
  scJackpots: Record<JackpotTier, JackpotData>;
  toggleOptIn: (currency: CurrencyType) => Promise<void>;
  incrementJackpot: (tier: JackpotTier, amount: number, currency: CurrencyType) => void;
  lastWinners: JackpotWinner[];
  totalPlayers: number;
  getJackpotsForCurrency: (currency: CurrencyType) => Record<JackpotTier, JackpotData>;
  currentWin: JackpotWin | null;
  clearCurrentWin: () => void;
  markCurrentWinAsSeen: () => Promise<void>;
  isOptedInForCurrency: (currency: CurrencyType) => boolean;
}

interface JackpotWinner {
  id: string;
  username: string;
  amount: number;
  date: string;
  game: string;
  tier: JackpotTier;
  currency_type: CurrencyType;
  state?: string;
}

const JackpotContext = createContext<JackpotContextType | undefined>(undefined);

export const useJackpot = () => {
  const context = useContext(JackpotContext);
  if (!context) {
    throw new Error('useJackpot must be used within a JackpotProvider');
  }
  return context;
};

export const JackpotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // GC Jackpots (will be loaded from database)
  const [gcJackpots, setGcJackpots] = useState<Record<JackpotTier, JackpotData>>({
    grand: {
      amount: 0,
      optInCost: 0,   // Will be loaded from group config
      isOptedIn: false,
      color: 'text-purple-500',
      participants: 0
    },
    major: {
      amount: 0,
      optInCost: 0,   // Will be loaded from group config
      isOptedIn: false,
      color: 'text-casino-gold',
      participants: 0
    },
    minor: {
      amount: 0,
      optInCost: 0,   // Will be loaded from group config
      isOptedIn: false,
      color: 'text-casino-green',
      participants: 0
    },
    mini: {
      amount: 0,
      optInCost: 0,   // Will be loaded from group config
      isOptedIn: false,
      color: 'text-casino-blue',
      participants: 0
    }
  });

  // SC Jackpots (will be loaded from database)
  const [scJackpots, setScJackpots] = useState<Record<JackpotTier, JackpotData>>({
    grand: {
      amount: 0,
      optInCost: 0,     // Will be loaded from group config
      isOptedIn: false,
      color: 'text-purple-500',
      participants: 0
    },
    major: {
      amount: 0,
      optInCost: 0,     // Will be loaded from group config
      isOptedIn: false,
      color: 'text-casino-gold',
      participants: 0
    },
    minor: {
      amount: 0,
      optInCost: 0,    // Will be loaded from group config
      isOptedIn: false,
      color: 'text-casino-green',
      participants: 0
    },
    mini: {
      amount: 0,
      optInCost: 0,     // Will be loaded from group config
      isOptedIn: false,
      color: 'text-casino-blue',
      participants: 0
    }
  });

  const [totalPlayers, setTotalPlayers] = useState(0); // Total unique users who have ever opted in

  const [lastWinners, setLastWinners] = useState<JackpotWinner[]>([]);
  const [currentWin, setCurrentWin] = useState<JackpotWin | null>(null);

  // Helper to map database name to tier
  const mapNameToTier = useCallback((name: string): JackpotTier | null => {
    const normalized = name.replace('_SC', '').toLowerCase();
    if (normalized === 'jackpot') return 'grand'; // Old JACKPOT -> new GRAND
    if (normalized === 'grand') return 'grand';
    if (normalized === 'major') return 'major';
    if (normalized === 'minor') return 'minor';
    if (normalized === 'mini') return 'mini';
    return null;
  }, []);

  // Helper to map recent wins to JackpotWinner format
  const mapRecentWins = useCallback((recentWins: any[]): JackpotWinner[] => {
    return recentWins.map((win: any) => {
      let tier: JackpotTier = 'mini';
      const tierName = win.jackpot_tier?.replace('_SC', '').toLowerCase() || 'mini';
      if (tierName === 'jackpot') {
        tier = 'grand';
      } else if (['grand', 'major', 'minor', 'mini'].includes(tierName)) {
        tier = tierName as JackpotTier;
      }

      return {
        id: win.id,
        username: win.username || 'Unknown User',
        amount: parseFloat(String(win.amount)) || 0,
        date: win.created_at,
        game: 'Slot Game',
        tier,
        currency_type: win.currency_type as CurrencyType,
        state: win.state || null
      };
    });
  }, []);

  // Helper to update jackpot state from response
  const updateJackpotsFromResponse = useCallback((response: any) => {
    if (!response.jackpots) return;

    // Update total players count
    if (response.totalPlayers !== undefined) {
      setTotalPlayers(response.totalPlayers);
    }

    // Separate GC and SC jackpots
    const gcJackpotsData = response.jackpots.filter((j: any) => !j.name.endsWith('_SC'));
    const scJackpotsData = response.jackpots.filter((j: any) => j.name.endsWith('_SC'));
    
    // Get group config opt-in costs
    const gcOptInCost = response.groupConfigs?.GC?.total_opt_in_cost || 0;
    const scOptInCost = response.groupConfigs?.SC?.total_opt_in_cost || 0;
    
    // Update GC jackpots
    setGcJackpots(prev => {
      const newGcJackpots = { ...prev };
      gcJackpotsData.forEach((jackpot: any) => {
        const tierName = mapNameToTier(jackpot.name);
        if (tierName && newGcJackpots[tierName]) {
          newGcJackpots[tierName].amount = parseFloat(String(jackpot.current_amount || '0'));
          newGcJackpots[tierName].optInCost = typeof gcOptInCost === 'number' ? gcOptInCost : parseFloat(String(gcOptInCost || '0'));
          newGcJackpots[tierName].participants = jackpot.participant_count || 0;
        }
      });
      return newGcJackpots;
    });
    
    // Update SC jackpots
    setScJackpots(prev => {
      const newScJackpots = { ...prev };
      scJackpotsData.forEach((jackpot: any) => {
        const tierName = mapNameToTier(jackpot.name);
        if (tierName && newScJackpots[tierName]) {
          newScJackpots[tierName].amount = parseFloat(String(jackpot.current_amount || '0'));
          newScJackpots[tierName].optInCost = typeof scOptInCost === 'number' ? scOptInCost : parseFloat(String(scOptInCost || '0'));
          newScJackpots[tierName].participants = jackpot.participant_count || 0;
        }
      });
      return newScJackpots;
    });
  }, [mapNameToTier]);

  // Helper to update preferences from response
  const updatePreferencesFromResponse = useCallback((preferences: any[]) => {
    // Initialize both currencies to false first
    setGcJackpots(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(tier => {
        updated[tier as JackpotTier] = { ...updated[tier as JackpotTier], isOptedIn: false };
      });
      return updated;
    });
    setScJackpots(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(tier => {
        updated[tier as JackpotTier] = { ...updated[tier as JackpotTier], isOptedIn: false };
      });
      return updated;
    });
    
    // Then update with actual preferences
    if (preferences && Array.isArray(preferences)) {
      preferences.forEach((pref: any) => {
        const currency = pref.currency_type as CurrencyType;
        const setterFunction = currency === 'GC' ? setGcJackpots : setScJackpots;
        const isOptedIn = pref.is_opted_in === true;
        
        setterFunction(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(tier => {
            updated[tier as JackpotTier] = { ...updated[tier as JackpotTier], isOptedIn };
          });
          return updated;
        });
      });
    }
  }, []);

  /**
   * Load all jackpot data using the unified /jackpot/lobby endpoint
   * This consolidates 4 separate API calls into 1:
   * - /jackpot (amounts, configs, participants)
   * - /jackpot/recent-wins
   * - /jackpot/currency-preferences
   * - /jackpot/unseen-wins
   */
  const loadJackpotLobbyData = useCallback(async () => {
    if (!user) return;

    try {
      const response = await apiClient.get('/jackpot/lobby');
      
      if (response.success) {
        // Update jackpot amounts and configs
        updateJackpotsFromResponse(response);
        
        // Update recent winners (for jackpot page)
        if (response.recentWins) {
          setLastWinners(mapRecentWins(response.recentWins));
        }
        
        // Update user preferences
        if (response.preferences) {
          updatePreferencesFromResponse(response.preferences);
        }
        
        // Check for unseen wins (for modal display)
        if (response.unseenWins?.length > 0) {
          setCurrentWin(response.unseenWins[0]);
        }
      }
    } catch (error: any) {
      if (error?.message?.includes('401') || error?.message?.includes('Invalid authentication')) {
        if (import.meta.env.DEV) {
          console.debug('Jackpot data requires authentication');
        }
        return;
      }
      if (import.meta.env.DEV) {
        console.error('Error loading jackpot lobby data:', error);
      }
    }
  }, [user, updateJackpotsFromResponse, mapRecentWins, updatePreferencesFromResponse]);

  /**
   * Load just jackpot amounts (for polling fallback)
   * Uses the old /jackpot endpoint since it doesn't need user-specific data
   */
  const loadJackpots = useCallback(async () => {
    try {
      const response = await apiClient.get('/jackpot');
      if (response.success) {
        updateJackpotsFromResponse(response);
      }
    } catch (error: any) {
      if (error?.message?.includes('401') || error?.message?.includes('Invalid authentication')) {
        return;
      }
      if (import.meta.env.DEV) {
        console.error('Error loading jackpots:', error);
      }
    }
  }, [updateJackpotsFromResponse]);

  /**
   * Load recent winners (for WebSocket win event refresh)
   */
  const loadRecentWinners = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await apiClient.get('/jackpot/recent-wins');
      if (response.success && response.recentWins) {
        setLastWinners(mapRecentWins(response.recentWins));
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('Error loading recent winners:', error);
      }
    }
  }, [user, mapRecentWins]);

  const getJackpotsForCurrency = (currency: CurrencyType): Record<JackpotTier, JackpotData> => {
    return currency === 'GC' ? gcJackpots : scJackpots;
  };

  const toggleOptIn = async (currency: CurrencyType) => {
    const setterFunction = currency === 'GC' ? setGcJackpots : setScJackpots;
    const currentJackpots = currency === 'GC' ? gcJackpots : scJackpots;
    
    // Check if user is opted into any jackpot of this currency
    const currentStatus = Object.values(currentJackpots).some(j => j.isOptedIn);
    const newOptInStatus = !currentStatus;
    
    // Update local state for all jackpots of this currency
    setterFunction(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(tier => {
        updated[tier as JackpotTier] = {
          ...updated[tier as JackpotTier],
          isOptedIn: newOptInStatus
        };
      });
      return updated;
    });
    
    // Save to database
    try {
      if (user) {
        const data = await apiClient.put('/jackpot/currency-preferences', {
          currencyType: currency,
          isOptedIn: newOptInStatus,
        });
        
        if (!data?.success) {
          // Revert local state on error
          setterFunction(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(tier => {
              updated[tier as JackpotTier] = {
                ...updated[tier as JackpotTier],
                isOptedIn: !newOptInStatus
              };
            });
            return updated;
          });
        }
      }
    } catch {
      // Revert local state on error
      setterFunction(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(tier => {
          updated[tier as JackpotTier] = {
            ...updated[tier as JackpotTier],
            isOptedIn: !newOptInStatus
          };
        });
        return updated;
      });
    }
  };

  const isOptedInForCurrency = (currency: CurrencyType): boolean => {
    const jackpots = currency === 'GC' ? gcJackpots : scJackpots;
    return Object.values(jackpots).some(j => j.isOptedIn);
  };

  const incrementJackpot = (tier: JackpotTier, amount: number, currency: CurrencyType) => {
    const setterFunction = currency === 'GC' ? setGcJackpots : setScJackpots;
    
    setterFunction(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        amount: prev[tier].amount + amount
      }
    }));
  };

  const clearCurrentWin = () => {
    setCurrentWin(null);
  };

  // Mark the current win as seen in the database and clear it
  const markCurrentWinAsSeen = async () => {
    if (currentWin?.id) {
      try {
        await apiClient.put(`/jackpot/wins/${currentWin.id}/seen`, {});
      } catch {
        // Silently fail - marking win as seen is not critical
      }
    }
    setCurrentWin(null);
  };

  /**
   * Main data loading effect - uses unified endpoint when user is logged in
   * No API calls for unauthenticated users (jackpot endpoints require auth)
   */
  useEffect(() => {
    if (user) {
      // User is logged in - use unified endpoint (1 API call instead of 4)
      loadJackpotLobbyData();
      
      // Poll jackpot amounts every 30 seconds as a fallback for WebSocket
      // Primary real-time updates come via WebSocket subscription to 'jackpot:update'
      const pollInterval = setInterval(() => {
        loadJackpots();
      }, 30000);
      
      return () => {
        clearInterval(pollInterval);
      };
    } else {
      // User not logged in - reset opt-in statuses, no API calls
      // (jackpot endpoints require authentication)
      setGcJackpots(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(tier => {
          updated[tier as JackpotTier] = { ...updated[tier as JackpotTier], isOptedIn: false };
        });
        return updated;
      });
      setScJackpots(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(tier => {
          updated[tier as JackpotTier] = { ...updated[tier as JackpotTier], isOptedIn: false };
        });
        return updated;
      });
    }
  }, [user, loadJackpotLobbyData, loadJackpots]);

  // WebSocket subscriptions for real-time updates
  useEffect(() => {
    if (!user) return;

    const token = storage.getItem('auth_token') || storage.getItem('token');
    if (!token) return;

    // Connect WebSocket
    websocketClient.connect(token).catch(console.error);

    // Subscribe to jackpot updates
    const unsubscribeJackpotUpdate = websocketClient.subscribe('jackpot:update', (data: any) => {
      // Map database name to tier (handle old JACKPOT -> new GRAND)
      const dbName = data.name.replace('_SC', '').toLowerCase();
      let tierName: JackpotTier | null = null;
      if (dbName === 'jackpot') tierName = 'grand';
      else if (dbName === 'grand') tierName = 'grand';
      else if (dbName === 'major') tierName = 'major';
      else if (dbName === 'minor') tierName = 'minor';
      else if (dbName === 'mini') tierName = 'mini';
      
      if (!tierName) return;
      
      const currency = data.name.endsWith('_SC') ? 'SC' : 'GC';
      const setterFunction = currency === 'GC' ? setGcJackpots : setScJackpots;
      
      // Parse the amount to ensure it's a proper number (WebSocket might send string)
      const parsedAmount = parseFloat(String(data.current_amount)) || 0;
      
      setterFunction(prev => {
        if (prev[tierName!]) {
          return {
            ...prev,
            [tierName!]: {
              ...prev[tierName!],
              amount: parsedAmount
            }
          };
        }
        return prev;
      });
    });

    // Subscribe to jackpot wins
    const unsubscribeJackpotWin = websocketClient.subscribe('jackpot:win', (data: any) => {
      // Only show the "You won" modal if the current user is the winner
      // The backend sends is_me=true only to the winning user's private room
      if (data.is_me) {
        // Parse the amount to ensure it's a proper number
        const parsedAmount = parseFloat(String(data.amount)) || 0;
        setCurrentWin({
          id: data.id,
          jackpot_tier: data.jackpot_tier,
          currency_type: data.currency_type,
          amount: parsedAmount,
          created_at: new Date().toISOString()
        });
      }
      
      // Reload recent winners for everyone (to update the winners list)
      loadRecentWinners();
    });

    return () => {
      unsubscribeJackpotUpdate();
      unsubscribeJackpotWin();
    };
  }, [user, loadRecentWinners]);

  return (
    <JackpotContext.Provider value={{
      gcJackpots,
      scJackpots,
      toggleOptIn,
      incrementJackpot,
      lastWinners,
      totalPlayers,
      getJackpotsForCurrency,
      currentWin,
      clearCurrentWin,
      markCurrentWinAsSeen,
      isOptedInForCurrency
    }}>
      {children}
    </JackpotContext.Provider>
  );
};